import React, { useState, useEffect } from 'react';
import '../styles/BillingPackages.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function BillingPackages() {
  const [packages, setPackages] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    // Get userId from localStorage
    const userData = localStorage.getItem('userAuth');
    if (userData) {
      const user = JSON.parse(userData);
      setUserId(user.id || user.RowKey); // Support both formats
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchPackages();
      fetchCurrentSubscription();
      fetchUsage();
    }
  }, [userId]);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/packages`);
      const data = await response.json();
      if (data.success) {
        setPackages(data.packages);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/user/subscription?userId=${userId}`);
      const data = await response.json();
      if (data.success && data.subscription) {
        setCurrentSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API_URL}/user/usage?userId=${userId}`);
      const data = await response.json();
      if (data.success && data.usage) {
        setUsage(data.usage);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleSelectPackage = (packageId) => {
    setSelectedPackage(packageId);
    setShowPaymentModal(true);
  };

  const handleSubscribe = async () => {
    try {
      const response = await fetch(`${API_URL}/subscriptions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          packageId: selectedPackage,
          billingCycle
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Subscription created! Please complete payment.');
        // In production, redirect to PayPal or show payment modal
        setShowPaymentModal(false);
        fetchCurrentSubscription();
      } else {
        alert(data.message || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getPackagePrice = (pkg) => {
    if (pkg.oneTime) {
      return pkg.pricing.oneTime;
    }
    return pkg.pricing[billingCycle];
  };

  const calculateSavings = (pkg) => {
    if (billingCycle === 'annual' && pkg.pricing.savings) {
      return pkg.pricing.savings.annual;
    }
    return 0;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading packages...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Billing & Packages</h1>
        <p style={styles.subtitle}>Choose the perfect plan for your research needs</p>
      </div>

      {/* Current Subscription Status */}
      {currentSubscription && (
        <div style={styles.currentPlanCard}>
          <div style={styles.currentPlanHeader}>
            <h3 style={styles.currentPlanTitle}>Current Plan</h3>
            <span style={styles.activeBadge}>Active</span>
          </div>
          <div style={styles.currentPlanBody}>
            <h2 style={styles.planName}>{currentSubscription.packageName}</h2>
            <p style={styles.planPrice}>
              {formatPrice(currentSubscription.price)} / {currentSubscription.billingCycle}
            </p>
            {usage && usage.submissionsLimit > 0 && (
              <div style={styles.usageBar}>
                <div style={styles.usageLabel}>
                  Submissions Used: {usage.submissionsUsed} / {usage.submissionsLimit}
                </div>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${(usage.submissionsUsed / usage.submissionsLimit) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}
            <p style={styles.renewalDate}>
              Renews on: {new Date(currentSubscription.endDate).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div style={styles.toggleContainer}>
        <button
          onClick={() => setBillingCycle('monthly')}
          style={{
            ...styles.toggleButton,
            ...(billingCycle === 'monthly' ? styles.toggleButtonActive : {})
          }}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle('annual')}
          style={{
            ...styles.toggleButton,
            ...(billingCycle === 'annual' ? styles.toggleButtonActive : {})
          }}
        >
          Annual
          <span style={styles.saveBadge}>Save up to ₹2,000</span>
        </button>
      </div>

      {/* Package Cards */}
      <div style={styles.packagesGrid}>
        {Object.values(packages).map((pkg) => {
          const price = getPackagePrice(pkg);
          const savings = calculateSavings(pkg);
          const isCurrent = currentSubscription?.packageId === pkg.id;

          return (
            <div
              key={pkg.id}
              style={{
                ...styles.packageCard,
                ...(pkg.bestValue ? styles.bestValueCard : {}),
                ...(isCurrent ? styles.currentPackageCard : {})
              }}
            >
              {pkg.bestValue && (
                <div style={styles.bestValueBadge}>BEST VALUE</div>
              )}
              {isCurrent && (
                <div style={styles.currentBadge}>CURRENT PLAN</div>
              )}

              <div style={styles.packageHeader}>
                <h3 style={styles.packageName}>{pkg.shortName}</h3>
                <p style={styles.packageDescription}>{pkg.description}</p>
              </div>

              <div style={styles.packagePricing}>
                <div style={styles.price}>{formatPrice(price)}</div>
                <div style={styles.pricePeriod}>
                  {pkg.oneTime ? 'One-time' : `per ${billingCycle}`}
                </div>
                {savings > 0 && (
                  <div style={styles.savings}>Save {formatPrice(savings)}</div>
                )}
              </div>

              <div style={styles.features}>
                {pkg.features.slice(0, 6).map((feature, index) => (
                  <div key={index} style={styles.feature}>
                    <span style={styles.checkmark}>✓</span>
                    {feature}
                  </div>
                ))}
                {pkg.features.length > 6 && (
                  <div style={styles.moreFeatures}>
                    +{pkg.features.length - 6} more features
                  </div>
                )}
              </div>

              <button
                onClick={() => handleSelectPackage(pkg.id)}
                disabled={isCurrent}
                style={{
                  ...styles.selectButton,
                  ...(isCurrent ? styles.selectButtonDisabled : {}),
                  ...(pkg.bestValue ? styles.selectButtonBestValue : {})
                }}
              >
                {isCurrent ? 'Current Plan' : pkg.oneTime ? 'Buy Now' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPaymentModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Complete Your Subscription</h2>
            <div style={styles.modalContent}>
              <p>Package: {packages[selectedPackage]?.name}</p>
              <p>Billing: {billingCycle}</p>
              <p style={styles.modalPrice}>
                Total: {formatPrice(getPackagePrice(packages[selectedPackage]))}
              </p>

              <div style={styles.paymentNote}>
                <p><strong>Note:</strong> PayPal integration is being set up.</p>
                <p>For now, this will create a pending subscription.</p>
              </div>

              <div style={styles.modalButtons}>
                <button onClick={handleSubscribe} style={styles.confirmButton}>
                  Confirm Subscription
                </button>
                <button onClick={() => setShowPaymentModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px'
  },
  title: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '10px'
  },
  subtitle: {
    fontSize: '18px',
    color: '#7f8c8d'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px',
    color: '#7f8c8d'
  },
  currentPlanCard: {
    backgroundColor: '#e8f5e9',
    border: '2px solid #27ae60',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '40px'
  },
  currentPlanHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  currentPlanTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#27ae60',
    margin: 0
  },
  activeBadge: {
    backgroundColor: '#27ae60',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  currentPlanBody: {
    marginTop: '16px'
  },
  planName: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2c3e50',
    margin: '0 0 8px 0'
  },
  planPrice: {
    fontSize: '18px',
    color: '#27ae60',
    fontWeight: '600',
    marginBottom: '16px'
  },
  usageBar: {
    marginTop: '16px'
  },
  usageLabel: {
    fontSize: '14px',
    color: '#2c3e50',
    marginBottom: '8px'
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#ecf0f1',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#27ae60',
    transition: 'width 0.3s ease'
  },
  renewalDate: {
    fontSize: '14px',
    color: '#7f8c8d',
    marginTop: '12px'
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '40px'
  },
  toggleButton: {
    padding: '12px 32px',
    border: '2px solid #ecf0f1',
    backgroundColor: 'white',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative'
  },
  toggleButtonActive: {
    backgroundColor: '#3498db',
    color: 'white',
    borderColor: '#3498db'
  },
  saveBadge: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '500',
    marginTop: '4px',
    opacity: 0.8
  },
  packagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginTop: '32px'
  },
  packageCard: {
    backgroundColor: 'white',
    border: '2px solid #ecf0f1',
    borderRadius: '12px',
    padding: '24px',
    position: 'relative',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer'
  },
  bestValueCard: {
    border: '2px solid #f39c12',
    boxShadow: '0 4px 12px rgba(243, 156, 18, 0.2)'
  },
  currentPackageCard: {
    border: '2px solid #27ae60',
    backgroundColor: '#f8fff9'
  },
  bestValueBadge: {
    position: 'absolute',
    top: '-12px',
    right: '20px',
    backgroundColor: '#f39c12',
    color: 'white',
    padding: '4px 16px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  currentBadge: {
    position: 'absolute',
    top: '-12px',
    right: '20px',
    backgroundColor: '#27ae60',
    color: 'white',
    padding: '4px 16px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  packageHeader: {
    marginBottom: '20px'
  },
  packageName: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '8px'
  },
  packageDescription: {
    fontSize: '13px',
    color: '#7f8c8d',
    lineHeight: '1.4'
  },
  packagePricing: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #ecf0f1'
  },
  price: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#2c3e50'
  },
  pricePeriod: {
    fontSize: '14px',
    color: '#7f8c8d',
    marginTop: '4px'
  },
  savings: {
    fontSize: '14px',
    color: '#27ae60',
    fontWeight: '600',
    marginTop: '8px'
  },
  features: {
    marginBottom: '24px'
  },
  feature: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#2c3e50',
    lineHeight: '1.4'
  },
  checkmark: {
    color: '#27ae60',
    fontWeight: 'bold',
    marginRight: '8px',
    flexShrink: 0
  },
  moreFeatures: {
    fontSize: '13px',
    color: '#3498db',
    fontStyle: 'italic',
    marginTop: '8px'
  },
  selectButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  selectButtonBestValue: {
    backgroundColor: '#f39c12'
  },
  selectButtonDisabled: {
    backgroundColor: '#95a5a6',
    cursor: 'not-allowed'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%'
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '24px'
  },
  modalContent: {
    fontSize: '16px',
    color: '#2c3e50'
  },
  modalPrice: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#27ae60',
    margin: '16px 0'
  },
  paymentNote: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
    fontSize: '14px'
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  confirmButton: {
    flex: 1,
    padding: '14px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  cancelButton: {
    flex: 1,
    padding: '14px',
    backgroundColor: '#ecf0f1',
    color: '#2c3e50',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default BillingPackages;
